import type { ApiToken, ApiTokenCreated } from '@fitness/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ArrowLeft,
  Check,
  Compass,
  Copy,
  KeyRound,
  Link2,
  Plus,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { EMPTY_SECONDARY_CLASS } from '@/components/emptyStateClasses';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Field, fieldAria } from '@/components/Field';
import { Input } from '@/components/Input';
import { McpConnectionSnippets } from '@/components/McpConnectionSnippets';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { api, describeError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { OAUTH } from '@/lib/i18n/oauth';
import { TOUR_UI } from '@/lib/i18n/tours';
import { useTour } from '@/lib/tour/context';
import { queryKeys } from '@/lib/queryClient';
import { SAVED, showToast } from '@/lib/toast';

export const Route = createFileRoute('/_app/settings/tokens')({
  component: TokensPage,
});

/** El nombre es lo único que pide el contrato para crear un token. */
const tokenNameSchema = z
  .string()
  .trim()
  .min(1, 'Ponle un nombre para reconocerlo.')
  .max(60, 'Como mucho 60 caracteres.');

const DATE_FORMAT = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function formatDate(value: string | null): string {
  if (value === null) return 'nunca';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'desconocido' : DATE_FORMAT.format(date);
}

function TokensPage() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  // Token recién creado: es la única vez que el servidor devuelve el secreto.
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);

  const { status } = useAuth();
  const tour = useTour();

  const tokensQuery = useQuery({
    queryKey: queryKeys.tokens,
    queryFn: () => api.me.listTokens(),
    // Al cerrar sesión esta pantalla sigue montada un instante: sin esto
    // volvería a pedir la lista ya sin token.
    enabled: status === 'authenticated',
  });

  const createMutation = useMutation({
    mutationFn: (tokenName: string) => api.me.createToken({ name: tokenName }),
    onSuccess: async (token) => {
      setCreated(token);
      setName('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.tokens });
    },
  });

  function handleCreate(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const parsed = tokenNameSchema.safeParse(name);
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message ?? 'Nombre no válido.');
      return;
    }
    setNameError(null);
    createMutation.mutate(parsed.data);
  }

  const tokens = tokensQuery.data ?? [];

  return (
    <>
      <PageHeader
        title="Tokens de acceso"
        subtitle="Las llaves y conexiones con las que Claude y otros asistentes de IA entran en tu cuenta."
        action={
          <Link
            to="/settings"
            aria-label="Volver a Ajustes"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div className="flex flex-col gap-4">
        <Card
          data-tour="token-create"
          title="Crear un token"
          description="El valor completo se muestra una sola vez, justo después de crearlo."
        >
          <form onSubmit={handleCreate} noValidate className="flex flex-col gap-3">
            {createMutation.isError ? (
              <ErrorBanner title="No se ha podido crear el token" error={createMutation.error} />
            ) : null}

            <Field
              htmlFor="tokenName"
              label="Nombre"
              error={nameError ?? undefined}
              hint="Por ejemplo: «Claude en el portátil» o «Báscula del salón»."
            >
              <Input
                {...fieldAria('tokenName', nameError ?? undefined, true)}
                type="text"
                placeholder="Nombre del cliente"
                maxLength={60}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  if (nameError !== null) setNameError(null);
                }}
              />
            </Field>

            <Button
              type="submit"
              size="lg"
              block
              loading={createMutation.isPending}
              icon={<Plus className="size-4" />}
            >
              Crear token
            </Button>
          </form>
        </Card>

        <section aria-labelledby="tokens-heading" data-tour="token-list">
          <h2 id="tokens-heading" className="mb-2 px-1 text-sm font-semibold text-ink-muted">
            Tus tokens ({tokens.length})
          </h2>

          {tokensQuery.isPending ? (
            <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
              <Spinner size="sm" />
              <span className="text-sm">Cargando tus tokens…</span>
            </div>
          ) : tokensQuery.isError ? (
            <ErrorBanner
              title="No hemos podido cargar los tokens"
              error={tokensQuery.error}
              onRetry={() => void tokensQuery.refetch()}
            />
          ) : tokens.length === 0 ? (
            <EmptyState
              icon={KeyRound}
              title="Todavía no tienes tokens ni conexiones"
              description="Un token es una llave para que Claude Code, Codex o Claude Desktop usen tu cuenta: créalo arriba y la app te dará los bloques listos para copiar. Para claude.ai no hace falta; se conecta iniciando sesión."
              action={
                <button
                  type="button"
                  className={EMPTY_SECONDARY_CLASS}
                  onClick={() => tour.start('claude')}
                >
                  <Compass className="size-4" aria-hidden="true" />
                  {TOUR_UI.emptyLaunch}
                </button>
              }
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {tokens.map((token) => (
                <TokenRow key={token.id} token={token} />
              ))}
            </ul>
          )}
        </section>
      </div>

      {created ? (
        <NewTokenDialog
          token={created}
          onClose={() => {
            // El diálogo ya dice que se ha creado; al cerrarlo se confirma que
            // queda en la lista (un aviso encima del diálogo taparía su cabecera).
            showToast(SAVED.tokenCreated);
            setCreated(null);
          }}
        />
      ) : null}
    </>
  );
}

// --- Fila de token --------------------------------------------------------

function TokenRow({ token }: { token: ApiToken }) {
  const queryClient = useQueryClient();
  // Una concesión OAuth no tiene secreto que enseñar: se marca en su lugar.
  const oauth = token.kind === 'oauth';
  const Icon = oauth ? Link2 : KeyRound;
  // Confirmación en línea: nada de `window.confirm`.
  const [confirming, setConfirming] = useState(false);

  const revokeMutation = useMutation({
    mutationFn: () => api.me.revokeToken(token.id),
    onSuccess: async () => {
      showToast(SAVED.tokenRevoked);
      await queryClient.invalidateQueries({ queryKey: queryKeys.tokens });
    },
  });

  return (
    <li className="rounded-2xl border border-line bg-surface-raised p-4">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 font-medium break-words">{token.name}</p>
          {oauth ? (
            <p className="mt-1">
              <span className="inline-flex items-center rounded-md whitespace-nowrap bg-brand/15 px-1.5 py-0.5 text-xs font-semibold text-brand">
                {OAUTH.grantBadge}
              </span>
            </p>
          ) : (
            <p className="mt-0.5 truncate font-mono text-xs text-ink-muted">{token.tokenPrefix}…</p>
          )}
          <p className="mt-1 text-xs text-ink-faint">
            Creado el {formatDate(token.createdAt)} · Último uso: {formatDate(token.lastUsedAt)}
          </p>
          {token.scopes.length > 0 ? (
            <ul className="mt-2 flex flex-wrap gap-1">
              {token.scopes.map((scope) => (
                <li
                  key={scope}
                  className="rounded-md bg-surface-overlay px-1.5 py-0.5 font-mono text-xs text-ink-muted"
                >
                  {scope}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {confirming ? null : (
          <Button
            variant="ghost"
            onClick={() => setConfirming(true)}
            aria-label={`Revocar el token ${token.name}`}
          >
            Revocar
          </Button>
        )}
      </div>

      {confirming ? (
        <div className="mt-3 rounded-xl border border-danger/40 bg-danger-surface p-3">
          <p className="text-sm">
            {oauth ? (
              OAUTH.revokeGrantConfirm(token.name)
            ) : (
              <>
                ¿Seguro que quieres revocar <strong>{token.name}</strong>? Lo que use este token
                dejará de funcionar al momento.
              </>
            )}
          </p>
          {revokeMutation.isError ? (
            <p role="alert" className="mt-2 text-sm text-danger">
              {describeError(revokeMutation.error)}
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <Button
              variant="danger"
              loading={revokeMutation.isPending}
              onClick={() => revokeMutation.mutate()}
            >
              Sí, revocar
            </Button>
            <Button
              variant="secondary"
              onClick={() => setConfirming(false)}
              disabled={revokeMutation.isPending}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

// --- Diálogo del token recién creado --------------------------------------

function NewTokenDialog({ token, onClose }: { token: ApiTokenCreated; onClose: () => void }) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  async function copy(): Promise<void> {
    setCopyError(null);
    try {
      // `navigator.clipboard` sólo existe en contextos seguros (https o
      // localhost); si no está, se pide copiar a mano.
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyError('Tu navegador no ha dejado copiar. Selecciona el token y cópialo a mano.');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 sm:items-center"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-tour="token-dialog"
        className="max-h-full w-full max-w-app overflow-y-auto rounded-2xl border border-line bg-surface-raised p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-lg font-semibold">
            Tu token está listo
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="flex size-11 shrink-0 items-center justify-center rounded-xl text-ink-muted transition-colors hover:bg-surface-overlay"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-3 flex items-start gap-2 rounded-xl border border-danger/40 bg-danger-surface p-3 text-sm">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden="true" />
          <p>
            Cópialo ahora: <strong>no se volverá a mostrar</strong>. El servidor sólo guarda su
            huella, así que si lo pierdes tendrás que crear otro.
          </p>
        </div>

        <div data-tour="token-secret">
          <p className="mt-4 text-sm text-ink-muted">
            Token de <strong className="text-ink">{token.name}</strong>
          </p>
          <code className="mt-1 block w-full break-all rounded-xl bg-surface p-3 font-mono text-sm text-brand select-all">
            {token.token}
          </code>

          {copyError ? (
            <p role="alert" className="mt-2 text-sm text-danger">
              {copyError}
            </p>
          ) : null}

          <div className="mt-4 flex flex-col gap-2">
            <Button
              size="lg"
              block
              onClick={() => void copy()}
              icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            >
              {copied ? 'Copiado' : 'Copiar token'}
            </Button>
          </div>
        </div>

        <McpConnectionSnippets key={token.id} token={token.token} />

        <div className="mt-4 flex flex-col gap-2">
          <Button variant="secondary" size="lg" block data-tour="token-done" onClick={onClose}>
            Ya lo he guardado
          </Button>
        </div>
      </div>
    </div>
  );
}
